import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';
import { submitLead } from '../../lib/bookDemo';
import './SlotBookingModal.css';

function isoTime(hour, minute) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export default function SlotBookingModal({ topic, dayLabel, isoDate, slot, onClose, onConfirmed }) {
  const overlayRef = useRef(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    setError('');

    if (!smsConsent) {
      setError('Please check the SMS consent box to confirm your booking.');
      return;
    }

    setSubmitting(true);
    try {
      await submitLead({
        fullName: form.fullName.value.trim(),
        email: form.email.value.trim(),
        phone: form.phone.value.trim() || null,
        company: null,
        message: null,
        topic,
        wantsDemo: true,
        demoDate: isoDate,
        demoStartTime: isoTime(slot.hour, slot.minute),
      });
      onConfirmed({ dayLabel, slotLabel: slot.label, email: form.email.value.trim() });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <div
      className="modal-overlay"
      ref={overlayRef}
      onMouseDown={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="modal-panel" role="dialog" aria-modal="true" aria-label="Confirm your demo booking">
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
          <X size={20} strokeWidth={2} />
        </button>

        <span className="eyebrow">Confirm your slot</span>
        <h3 className="modal-title">{dayLabel} at {slot.label}</h3>
        <p className="modal-sub">Tell us where to send the confirmation.</p>

        <form className="modal-form" onSubmit={handleSubmit}>
          <label className="field">
            <span>Full name</span>
            <input type="text" name="fullName" required placeholder="Jordan Lee" autoFocus />
          </label>
          <label className="field">
            <span>Email</span>
            <input type="email" name="email" required placeholder="jordan@company.com" />
          </label>
          <label className="field">
            <span>Mobile number</span>
            <input type="tel" name="phone" required placeholder="+1 (___) ___-____" />
          </label>

          <label className="sms-consent-label">
            <input
              type="checkbox"
              checked={smsConsent}
              onChange={(e) => setSmsConsent(e.target.checked)}
            />
            <span>
              By checking this box, I agree to receive SMS messages from Tecnvirons (such as
              appointment reminders and confirmations) at the phone number provided. Message
              frequency varies and may be recurring. Message and data rates may apply. Reply STOP
              to opt out at any time, or HELP for help. See our{' '}
              <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</Link> and{' '}
              <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>.
            </span>
          </label>

          {error && <p className="form-error">{error}</p>}

          <button type="submit" className="btn btn-primary submit-btn" disabled={submitting}>
            {submitting ? 'Booking…' : 'Confirm booking'}
            <ArrowRight size={18} strokeWidth={2} />
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
